// Micro Kart Racers Client (Multi-Track Tournament)
import { sounds } from '../sound.js';

export class KartGameClient {
  constructor(socket, containerEl) {
    this.socket = socket;
    this.container = containerEl;
    this.karts = {};
    this.track = null;
    this.input = { forward: false, backward: false, left: false, right: false };

    this.render();
    this.setupSocketEvents();
    this.setupControls();
  }

  render() {
    this.container.innerHTML = `
      <div class="kart-wrapper">
        <div class="kart-header">
          <div class="kart-meta">
            <h2 id="kart-stage-title">🏎️ STAGE 1: NEON SPEEDWAY</h2>
            <span class="kart-sub">WASD/Arrows to Steer & Accelerate • Hit Yellow Boost Pads • Top drivers advance!</span>
          </div>
          <div id="kart-lap-badge" class="kart-badge">LAP 1/3</div>
        </div>

        <div class="kart-canvas-box">
          <canvas id="kart-canvas" width="900" height="600"></canvas>
        </div>

        <!-- Virtual Touch D-Pad for Mobile Players -->
        <div class="mobile-touch-bar">
          <div class="touch-dpad-cluster">
            <button id="touch-kart-left" class="btn-virtual-touch">◀</button>
            <button id="touch-kart-right" class="btn-virtual-touch">▶</button>
          </div>
          <div class="touch-action-cluster">
            <button id="touch-kart-brake" class="btn-virtual-touch">🔽</button>
            <button id="touch-kart-gas" class="btn-virtual-touch btn-virtual-action">⚡ GAS</button>
          </div>
        </div>

        <!-- Stage Elimination Notice -->
        <div id="kart-elim-banner" class="kart-elim-banner modal-hidden">
          <h3 id="kart-elim-text">STAGE FINISHED!</h3>
        </div>
      </div>
    `;

    this.canvas = document.getElementById('kart-canvas');
    this.ctx = this.canvas.getContext('2d');
  }

  setupControls() {
    const handleKey = (e, isDown) => {
      let changed = false;
      const key = e.key ? e.key.toLowerCase() : '';
      if (e.code === 'KeyW' || key === 'w' || e.code === 'ArrowUp') { this.input.forward = isDown; changed = true; }
      if (e.code === 'KeyS' || key === 's' || e.code === 'ArrowDown') { this.input.backward = isDown; changed = true; }
      if (e.code === 'KeyA' || key === 'a' || e.code === 'ArrowLeft') { this.input.left = isDown; changed = true; }
      if (e.code === 'KeyD' || key === 'd' || e.code === 'ArrowRight') { this.input.right = isDown; changed = true; }

      if (changed) {
        this.socket.emit('kart_input', this.input);
      }
    };

    const attachTouchBtn = (btnId, keyName) => {
      const btn = document.getElementById(btnId);
      if (!btn) return;
      const setPressed = (pressed) => {
        if (this.input[keyName] !== pressed) {
          this.input[keyName] = pressed;
          this.socket.emit('kart_input', this.input);
        }
      };
      btn.addEventListener('touchstart', (e) => { e.preventDefault(); setPressed(true); }, { passive: false });
      btn.addEventListener('touchend', (e) => { e.preventDefault(); setPressed(false); }, { passive: false });
      btn.addEventListener('mousedown', () => setPressed(true));
      btn.addEventListener('mouseup', () => setPressed(false));
      btn.addEventListener('mouseleave', () => setPressed(false));
    };

    attachTouchBtn('touch-kart-left', 'left');
    attachTouchBtn('touch-kart-right', 'right');
    attachTouchBtn('touch-kart-gas', 'forward');
    attachTouchBtn('touch-kart-brake', 'backward');

    this.keyDownHandler = (e) => handleKey(e, true);
    this.keyUpHandler = (e) => handleKey(e, false);

    window.addEventListener('keydown', this.keyDownHandler);
    window.addEventListener('keyup', this.keyUpHandler);
  }

  setupSocketEvents() {
    this.socket.on('kart_trackStarted', (data) => {
      this.track = data.track;
      this.karts = data.karts;
      const title = document.getElementById('kart-stage-title');
      const banner = document.getElementById('kart-elim-banner');
      if (title) title.innerText = data.track.name;
      if (banner) banner.classList.add('modal-hidden');
      sounds.playChime();
      this.draw();
    });

    this.socket.on('kart_state', (data) => {
      this.karts = data.karts;
      this.track = data.track;

      const myKart = this.karts[this.socket.id];
      const lapBadge = document.getElementById('kart-lap-badge');
      if (lapBadge && myKart) {
        lapBadge.innerText = myKart.isFinished ? '🏁 FINISHED!' : `LAP ${Math.min(3, myKart.lap)}/3`;
      }

      this.draw();
    });

    this.socket.on('kart_stageElimination', (data) => {
      sounds.playExplosion();
      const banner = document.getElementById('kart-elim-banner');
      const text = document.getElementById('kart-elim-text');
      if (banner && text) {
        text.innerText = `🚨 ${data.eliminatedName} was eliminated! Advancing to Stage ${data.nextStage}...`;
        banner.classList.remove('modal-hidden');
      }
    });

    this.socket.on('kart_gameOver', (data) => {
      sounds.playVictory();
      alert(`🏆 GRAND TOURNAMENT CHAMPION: ${data.winner?.name || 'Driver'}!`);
    });
  }

  draw() {
    if (!this.ctx || !this.track) return;
    const t = this.track;

    // Grass / Outer Canvas
    this.ctx.fillStyle = '#06130d';
    this.ctx.fillRect(0, 0, this.canvas.width, this.canvas.height);

    // Neon Canvas Boundary Wall
    this.ctx.strokeStyle = '#ef4444';
    this.ctx.lineWidth = 6;
    this.ctx.strokeRect(4, 4, this.canvas.width - 8, this.canvas.height - 8);

    // Asphalt Track Path (Oval)
    this.ctx.strokeStyle = '#1e293b';
    this.ctx.lineWidth = t.trackWidth;
    this.ctx.beginPath();
    this.ctx.ellipse(t.cx, t.cy, t.rx, t.ry, 0, 0, Math.PI * 2);
    this.ctx.stroke();

    // Track Outer Neon Border
    this.ctx.strokeStyle = '#38bdf8';
    this.ctx.lineWidth = 3;
    this.ctx.beginPath();
    this.ctx.ellipse(t.cx, t.cy, t.rx + t.trackWidth / 2, t.ry + t.trackWidth / 2, 0, 0, Math.PI * 2);
    this.ctx.stroke();

    // Track Inner Neon Border
    this.ctx.strokeStyle = '#38bdf8';
    this.ctx.beginPath();
    this.ctx.ellipse(t.cx, t.cy, Math.max(10, t.rx - t.trackWidth / 2), Math.max(10, t.ry - t.trackWidth / 2), 0, 0, Math.PI * 2);
    this.ctx.stroke();

    // Start / Finish Line
    this.ctx.strokeStyle = '#ffffff';
    this.ctx.setLineDash([8, 8]);
    this.ctx.lineWidth = 8;
    this.ctx.beginPath();
    this.ctx.moveTo(t.startLine.x, t.startLine.y1);
    this.ctx.lineTo(t.startLine.x, t.startLine.y2);
    this.ctx.stroke();
    this.ctx.setLineDash([]);

    // Boost Pads
    t.boostPads.forEach((bp) => {
      this.ctx.fillStyle = '#facc15';
      this.ctx.shadowColor = '#facc15';
      this.ctx.shadowBlur = 14;
      this.ctx.beginPath();
      this.ctx.arc(bp.x, bp.y, bp.r, 0, Math.PI * 2);
      this.ctx.fill();
    });
    this.ctx.shadowBlur = 0;

    // Draw Karts
    for (const id in this.karts) {
      const k = this.karts[id];
      if (k.isEliminated) continue;

      this.ctx.save();
      this.ctx.translate(k.x, k.y);
      this.ctx.rotate(k.angle);

      // Kart Chassis
      this.ctx.fillStyle = k.color;
      this.ctx.shadowColor = k.color;
      this.ctx.shadowBlur = 10;
      this.ctx.fillRect(-14, -9, 28, 18);

      // Tires
      this.ctx.fillStyle = '#0f172a';
      this.ctx.fillRect(-12, -12, 8, 4);
      this.ctx.fillRect(4, -12, 8, 4);
      this.ctx.fillRect(-12, 8, 8, 4);
      this.ctx.fillRect(4, 8, 8, 4);

      this.ctx.restore();

      // Driver Name & Lap
      this.ctx.fillStyle = '#ffffff';
      this.ctx.font = 'bold 11px Rajdhani, sans-serif';
      this.ctx.textAlign = 'center';
      this.ctx.fillText(`${k.name} (L${Math.min(3, k.lap)}/3)`, k.x, k.y - 18);
    }
  }

  destroy() {
    if (this.keyDownHandler) window.removeEventListener('keydown', this.keyDownHandler);
    if (this.keyUpHandler) window.removeEventListener('keyup', this.keyUpHandler);
    this.socket.off('kart_trackStarted');
    this.socket.off('kart_state');
    this.socket.off('kart_stageElimination');
    this.socket.off('kart_gameOver');
  }
}
